import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Rive, RuntimeLoader } from '@rive-app/canvas';

import { AppVersionService } from '@spaces-ui/app-version';
import { ElectronServiceBus } from '@spaces-ui/electron-service-bus';
import { LocationConfigService } from '@spaces-ui/location';
import { LoggerService } from '@spaces-ui/logger';

@Component({
  selector: 'mdw-rive',
  templateUrl: './rive.html',
  styleUrls: ['./rive.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class MdwRive implements OnDestroy {
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private locationService = inject(LocationConfigService);
  private electronServiceBus = inject(ElectronServiceBus);
  private appVersionService = inject(AppVersionService);
  private logger = inject(LoggerService);

  private readonly MAX_RETRIES = 2;
  private retryCount = signal(0);
  public hasError = signal(false);

  public state = input.required<number>();
  public fileName = input.required<string>();
  public artboardName = input.required<string>();
  public stateMachineName = input.required<string>();
  public inputName = input.required<string>();
  public height = input<number>(32);
  public width = input<number>(32);
  public play = input<boolean>(true);

  public canvasRef = viewChild('canvasEl', { read: ElementRef });

  public config = computed(() => {
    const state = this.state();
    const fileName = this.fileName();
    const artboardName = this.artboardName();
    const stateMachineName = this.stateMachineName();
    const inputName = this.inputName();
    const play = this.play() ?? true;

    if (state === undefined || !fileName || !artboardName || !stateMachineName || !inputName) {
      return undefined;
    }

    return {
      state,
      fileName,
      artboardName,
      stateMachineName,
      inputName,
      play,
    };
  });

  private rive?: Rive;

  public constructor() {
    const version = this.appVersionService.clientVersion() ?? Date.now();
    const wasmUrl = `${this.locationService.assetsLocation}/rive/rive.wasm?v=${version}`;
    RuntimeLoader.setWasmUrl(wasmUrl);

    effect(() => {
      const height = this.height();
      const width = this.width();
      this.elementRef.nativeElement.style.height = `${height}px`;
      this.elementRef.nativeElement.style.width = `${width}px`;
    });

    effect(() => {
      const canvasEl = this.canvasRef();
      const config = this.config();
      const hasError = this.hasError();
      const retryCount = this.retryCount();

      if (!config || !canvasEl || this.rive || hasError) {
        return;
      }

      this.rive = new Rive({
        src: `assets/rive/${config.fileName}.riv`,
        canvas: canvasEl.nativeElement,
        autoplay: config.play,
        artboard: config.artboardName,
        stateMachines: config.stateMachineName,
        onLoad: () => {
          this.rive?.resizeDrawingSurfaceToCanvas();
          this.setState(config.state, config.stateMachineName, config.inputName);
          this.retryCount.set(0);
        },
        onLoadError: err => {
          this.logger.error('Rive animation load error', {
            fileName: config.fileName,
            error: err,
            attempt: retryCount + 1,
          });

          if (retryCount < this.MAX_RETRIES) {
            this.rive?.cleanup();
            this.rive = undefined;
            // Use setTimeout to ensure retry happens in a new macrotask
            // after the current error handling completes
            setTimeout(() => this.retryCount.update(c => c + 1), 500);
          } else {
            this.hasError.set(true);
          }
        },
      });
    });

    effect(() => {
      const state = this.state();
      const inputName = this.inputName();
      const stateMachineName = this.stateMachineName();

      this.setState(state, stateMachineName, inputName);
    });
  }

  public ngOnDestroy() {
    this.rive?.cleanup();
  }

  private setState(state: number, stateMachineName: string, inputName: string) {
    if (!this.rive) {
      return;
    }

    const inputs = this.rive.stateMachineInputs(stateMachineName);

    if (!inputs) {
      return;
    }

    const input = inputs.find(i => i.name === inputName);
    if (!input) {
      return;
    }

    input.value = state;
  }
}
